<template>
  <div
    class="ten-cascader-panel"
    v-if="!_showFilter"
  >
    <ten-cascader-menu
      ref="cascaderMenus"
      :options="model.children"
      :key="model.key + '-cascader-menu-' + index"
      :index="index"
      :loading="model.loading"
      :title="getMenuTitle(model, index)"
      @menuBlur="dir => onMenuBlur(index, dir)"
      v-for="(model, index) in expandedNodes"
    />
  </div>
  <div
    class="ten-cascader-panel"
    v-else
  >
    <ten-cascader-menu
      :options="filteredOptions"
      :render-label="getLabelPathStrByModel"
    />
  </div>
</template>
<script>
import isEqual from 'lodash/isEqual';
import TreeModel from '../tree/tree-model';
import TenCascaderMenu from './cascader-menu';
import { isEmpty } from '../scripts/utils/helper';

const name = 'ten-cascader-panel';

export default {
  name,
  components: {
    TenCascaderMenu,
  },
  inject: {
    tenCascader: {
      from: 'tenCascader',
      default: null,
    },
  },
  provide() {
    return {
      tenCascaderPanel: this,
    };
  },
  props: {
    /**
     * v-model 当前值，对应事件 change
     */
    value: [String, Number, Array],
    /**
     * 非受控时，可传入默认值
     */
    defaultValue: [String, Number, Array, Object],
    /**
     * 筛选结果分割符
     */
    separator: {
      type: String,
      default: '/',
    },
    /**
     * 选项数据源
     */
    options: Array,
    /**
     * @ignore
     */
    onOptionsUpdate: Function,
    /**
     * 选项 title，自定义每个选项列表的标题
     */
    optionsTitle: [Array, Function],
    /**
     * 是否多选
     */
    multiple: Boolean,
    /**
     * 每个选项都独立可选，而非只有叶子节点可选
     */
    independent: Boolean,
    /**
     * 指定选项的 value 在 data 中的键值
     */
    optionValue: {
      type: [String, Function],
      default: 'value',
    },
    /**
     * 指定选项的 label 在 data 中的键值
     */
    optionLabel: {
      type: [String, Function],
      default: 'label',
    },
    /**
     * 指定选项的 children 在 data 中的键值
     */
    optionChildren: {
      type: [String, Function],
      default: 'children',
    },
    /**
     * 自定义渲染 option 函数，不适用于筛选节点
     */
    renderOption: Function,
    /**
     * 自定义加载子节点方法，需设置需要动态加载的节点 children 为 true
     * (data, dataPath) => Promise
     */
    loadChildren: Function,
    /**
     * 是否根据选项自动加载子节点（比如初始化 value 有值的时候）
     */
    autoLoadChildren: {
      type: Boolean,
      default: true,
    },
    /**
     * 子菜单的展开方式
     * @member click | hover
     */
    expandTrigger: {
      type: String,
      default: 'click',
    },
    /**
     * @ignore
     */
    filterText: {
      type: String,
      default: '',
    },
    /**
     * @ignore
     */
    filterOption: [Boolean, Function],
    /**
     * 仅子节点可选（一般在 multiple 模式下使用）
     */
    onlyLeafSelectable: Boolean,
    /**
     * 结果为空时提示文字，支持同名slot
     */
    emptyTip: {
      type: String,
      default: '暂无数据',
    },
  },
  data() {
    return {
      popupShow: false,
      valueDataMap: {},
      treeModel: null,
      expandedNodes: [],
      expandedPath: [],
      filteredOptions: [],
    };
  },
  computed: {
    _dataValue() {
      return this.transformValue(this.value);
    },
    _selectMode() {
      return this.independent ? 'independent' : 'default';
    },
    _selectedValueMode() {
      return this.independent ? 'all' : 'onlyLeaf';
    },
    _filterText() {
      return this.filterText.trim();
    },
    _showFilter() {
      return this.filterOption && this._filterText;
    },
  },
  watch: {
    _dataValue(v) {
      if (this.loadChildren && this.autoLoadChildren) {
        if (v.length > 0) {
          const [expandedPath] = v.slice(-1);
          this.expandedPath = expandedPath;
        }
        this.loadPathsOptions().then(() => {
          this.treeModel.setSelected(v);
          this.treeModel.applySelected();
        });
      } else {
        if (v.length > 0) {
          const [expandedPath] = v.slice(-1);
          this.expandedPath = expandedPath;
          this.updateExpandedNodes();
        }
        this.treeModel.setSelected(v);
        this.treeModel.applySelected();
      }
    },
    options: {
      handler() {
        if (this.inited) {
          this.initTreeModel();
        }
      },
      deep: true,
    },
    _filterText(v) {
      this.filteredOptions = this.getFilteredOptions(v);
    },
  },
  created() {
    this.loadErrorPaths = [];

    if (this._dataValue.length > 0) {
      const [expandedPath] = this._dataValue.slice(-1);
      this.expandedPath = expandedPath;
    }
    this.initTreeModel(this.options);
    if (this.loadChildren && this.autoLoadChildren) {
      this.loadPathsOptions().then(() => {
        if (!this.inited) {
          this.inited = true;
          this.initTreeModel(this.options);
        }
      });
    } else {
      this.inited = true;
    }
  },
  methods: {
    initTreeModel() {
      const treeModel = new TreeModel();
      const getKey = data => data[this.optionValue];
      const getChildren = data => data[this.optionChildren];
      treeModel.buildFromArray(this.options, getKey, getChildren);
      treeModel.setConfig({ selectMode: this._selectMode });
      treeModel.setSelected(this._dataValue);
      treeModel.applySelected();
      this.treeModel = treeModel;
      this.updateExpandedNodes();
      this.onOptionsUpdate && this.onOptionsUpdate();
    },
    // Load options based on value
    loadPathsOptions() {
      return new Promise((resolve) => {
        if (!this.loadChildren) {
          resolve();
          return;
        }
        const paths = this._dataValue.filter(p => !this.loadErrorPaths.find(eP => isEqual(p, eP)));
        const promises = [];
        for (let i = 0; i < paths.length; i++) {
          promises.push(new Promise(pathResolve => { // eslint-disable-line
            this.loadPathOptions(paths[i], pathResolve);
          }));
        }
        setTimeout(() => {
          resolve();
        }, 3000);

        return Promise.all(promises).then(() => {
          resolve();
        });
      });
    },
    loadPathOptions(path, done) {
      const dataPath = this.getDataPathByPath(path);
      if (dataPath.length > 0) {
        const lastData = dataPath[dataPath.length - 1];
        const children = lastData[this.optionChildren];
        if (children && !Array.isArray(children)) {
          const p = this.loadChildren(lastData, dataPath);
          if (typeof p === 'object' && p.then) {
            p.then((result) => {
              lastData[this.optionChildren] = result;
              this.loadPathOptions(path, done);
            }, () => {
              this.loadErrorPaths.push(path);
              done(1);
            });
          } else {
            done(2);
          }
        } else {
          done(3);
        }
      } else {
        done(4);
      }
    },
    getDataPathByPath(path) {
      const dataPath = [];
      let children = this.options;

      for (let i = 0; i < path.length && Array.isArray(children); i++) {
        const key = path[i];
        const foundData = children.find(child => child[this.optionValue] === key);
        if (foundData) {
          dataPath.push(foundData);
          children = foundData[this.optionChildren];
        }
      }

      return dataPath;
    },
    getLabelPathStr(path) {
      const nodeModel = this.treeModel.getNodeByPath(path);
      if (nodeModel) {
        return this.getLabelPathStrByModel(nodeModel);
      }
      return '';
    },
    getLabelPathStrByModel(nodeModel) {
      const nodePath = nodeModel.getNodePath();
      const dataPath = nodePath.map(node => node.data);
      const labelPath = dataPath
        .map(data => data[this.optionLabel])
        .join(this.separator);
      return labelPath;
    },
    getFilteredOptions(text) {
      const filteredOptions = [];
      const defaultFilterFunc = (node) => {
        const labelPath = this.getLabelPathStrByModel(node);
        return labelPath.toLowerCase().indexOf(text.toLowerCase()) > -1;
      };
      this.treeModel.walk((node) => {
        if (!this.independent && !node.isLeaf) {
          return;
        }
        const { data } = node;
        if (
          typeof this.filterOption === 'function'
          && this.filterOption(data)
        ) {
          filteredOptions.push(node);
        } else if (defaultFilterFunc(node)) {
          filteredOptions.push(node);
        }
      });

      return filteredOptions;
    },
    getMenuTitle(model, index) {
      if (Array.isArray(this.optionsTitle) && this.optionsTitle[index]) {
        return this.optionsTitle[index];
      }
      if (typeof this.optionsTitle === 'function') {
        const { data } = model;
        const options = index === 0 ? this.options : data[this.optionChildren];
        /**
         * optionsTitle
         */
        return this.optionsTitle(index, options, data);
      }
      return '';
    },
    focus() {
      if (this.$refs.cascaderMenus && this.$refs.cascaderMenus[0]) {
        this.$refs.cascaderMenus[0].focus(-1);
      }
    },
    blur() {
      if (this.$refs.cascaderMenus) {
        this.$refs.cascaderMenus.forEach(menu => menu.blur());
      }
    },
    transformValue(v) {
      let transformedValue;
      if (!this.multiple) {
        transformedValue = !isEmpty(v) ? [v] : [];
      } else {
        transformedValue = Array.isArray(v) ? v : [];
      }
      transformedValue = transformedValue
        .map((item) => {
          if (Array.isArray(item) && item.length > 0) {
            return item;
          }
          return undefined;
        })
        .filter(item => item);
      return transformedValue;
    },
    reverseValue(selectedPaths) {
      let reversedValue = selectedPaths;

      if (!this.multiple) {
        reversedValue = reversedValue.length > 0 ? reversedValue[0] : null;
      }
      return reversedValue;
    },
    updateExpandedNodes(model) {
      let triggerNode = model;
      if (!triggerNode) {
        triggerNode = this.treeModel.getNodeByPath(this.expandedPath);
      }
      if (!triggerNode) {
        this.expandedNodes = [this.treeModel];
        return;
      }
      const nodePath = triggerNode.getNodePath();
      const expandedNodes = nodePath;

      for (let i = 0; i < expandedNodes.length; i++) {
        const node = expandedNodes[i];
        if (node.isLeaf || node.data.disabled) {
          expandedNodes.length = i;
        }
      }
      expandedNodes.unshift(this.treeModel);
      this.expandedPath = triggerNode.path;
      this.expandedNodes = expandedNodes;
    },
    loadNodeChildren(nodeModel) {
      if (nodeModel.data[this.optionsChildren]) {

      }
    },
    onNodeExpand(treeNodeModel) {
      this.updateExpandedNodes(treeNodeModel);

      const rawData = treeNodeModel.data;
      if (
        this.loadChildren
        && !treeNodeModel.isLeaf
        && !Array.isArray(rawData[this.optionChildren])
        && !treeNodeModel.loading
      ) {
        treeNodeModel.loading = true;
        const nodePath = treeNodeModel.getNodePath();
        const dataPath = nodePath.map(node => node.data);
        const rawData = treeNodeModel.data;
        const p = this.loadChildren(rawData, dataPath);
        if (typeof p === 'object' && typeof p.then === 'function') {
          p.then(
            (children) => {
              treeNodeModel.loading = false;
              rawData[this.optionChildren] = children;
            },
            () => {
              treeNodeModel.loading = false;
              this.updateExpandedNodes();
            }
          );
        } else {
          treeNodeModel.loading = false;
        }
      }
    },
    onNodeSelect(treeNodeModel, selected) {
      const { path } = treeNodeModel;
      let selectedPaths;
      if (!this.multiple) {
        selectedPaths = [path];
      } else {
        this.treeModel.handleSelectedChange(path, selected);
        selectedPaths = this.treeModel.getSelectedPaths(this._selectedValueMode);
      }
      const reversedValue = this.reverseValue(selectedPaths);
      this.$emit('change', reversedValue);
    },
    onMenuBlur(triggerMenuIndex, dir) {
      const triggerMenu = this.$refs.cascaderMenus[triggerMenuIndex];
      const { cascaderMenus } = this.$refs;
      let index = cascaderMenus.indexOf(triggerMenu);
      if (index > -1) {
        index += dir === 'left' ? -1 : 1;
        if (cascaderMenus[index]) {
          triggerMenu.blur();
          cascaderMenus[index].focus();
        }
      }
    },
    renderOptionContent(h, model) {
      if (this._showFilter) {
        return this._v(`${this.getLabelPathStrByModel(model)}`);
      }

      const option = model.data;
      if (this.renderOption) {
        return this.renderOption(h, { option });
      } if (this.$scopedSlots.option) {
        return this.$scopedSlots.option({ option });
      }
      return this._v(`${option[this.optionLabel]}`);
    },
    renderEmptyTip() {
      if (this.tenCascader) return this.tenCascader.renderEmptyTip();

      const parentSlots = this.$parent.$slots;
      const defaultSlot = <span slot="emptyTip">{this.emptyTip}</span>;
      return parentSlots.emptyTip ? parentSlots.emptyTip[0] : defaultSlot;
    },
  },
};
</script>
